import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../App";
import { login, logout, forgotPassword } from "../../services/api";

const s = {
  page: { 
    display: "flex", 
    justifyContent: "center", 
    alignItems: "center", 
    minHeight: "calc(100vh - 64px)", 
    padding: 24,
  },
  card: {
    background: "#2d8641", 
    borderRadius: 8, 
    padding: "36px 36px", 
    width: "100%", 
    maxWidth: 420,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)", 
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: "#fff",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    boxSizing: "border-box"
  },
  logo: {
    maxWidth: "100%",
    height: "auto",
    marginBottom: 24
  },
  title: { 
    fontSize: 20, 
    fontWeight: "bold", 
    marginBottom: 20,
    textAlign: "center",
    margin: "0 0 20px 0"
  },
  textRow: {
    fontSize: 15,
    textAlign: "center",
    marginBottom: 4,
    lineHeight: "1.4"
  },
  textRowGroup: {
    marginBottom: 28
  },
  input: {
    width: "100%", 
    padding: "14px 16px", 
    border: "none", 
    borderRadius: 8,
    fontSize: 15, 
    outline: "none", 
    marginBottom: 16,
    boxSizing: "border-box",
    color: "#333",
    background: "#fff"
  },
  forgotPasswordRow: {
    width: "100%",
    textAlign: "left",
    marginBottom: 28
  },
  forgotPasswordLink: {
    color: "#e2e8f0",
    fontSize: 14,
    textDecoration: "none",
  },
  btn: {
    width: "100%", 
    padding: "14px", 
    background: "#fabd05", 
    color: "#000", 
    border: "none", 
    borderRadius: 8, 
    fontSize: 16, 
    fontWeight: "bold",
    cursor: "pointer", 
    transition: "background 0.2s",
  },
  btnDisabled: { opacity: 0.6, cursor: "not-allowed" },
  error: {
    background: "#fef2f2", 
    border: "1px solid #fecaca", 
    borderRadius: 8, 
    padding: "10px 14px",
    fontSize: 13, 
    color: "#dc2626", 
    marginBottom: 18,
    width: "100%",
    boxSizing: "border-box",
    textAlign: "center"
  }
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [searchParams] = useSearchParams();
  const switchingAccount = searchParams.get("switchAccount") === "1";
  const [sessionReady, setSessionReady] = useState(!switchingAccount);
  const { loginUser, logoutUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!switchingAccount) return;
    let mounted = true;
    logout()
      .catch(() => {})
      .finally(() => {
        logoutUser();
        if (mounted) setSessionReady(true);
      });
    return () => { mounted = false; };
  }, [logoutUser, switchingAccount]);

  const handleForgotPassword = async () => {
    if (!resetEmail) return;
    setResetLoading(true);
    try {
      await forgotPassword(resetEmail);
      setResetSent(true);
    } catch {
      setResetSent(true); // still show confirmation to avoid enumeration
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await login(email, password);
      loginUser(res.data.user);
      navigate(["admin", "collaborator"].includes(res.data.user?.role)
        ? "/admin"
        : "/dashboard");
    } catch (err) {
      setError(err.response?.data?.error || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <form style={s.card} onSubmit={handleSubmit} className="animate-fade-in-up">
        <img src="/images/logo.png" alt="SPEEDiagnostix" style={s.logo} />
        
        <h1 style={s.title}>Dashboard Login</h1>
        
        <div style={s.textRowGroup}>
          <div style={s.textRow}>The dashboard is under construction.</div>
          <div style={s.textRow}>Administrators and invited alpha testers can log in.</div>
        </div>

        {error && <div style={s.error}>{error}</div>}

        <input
          style={s.input}
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Type your email"
          required
        />

        <input
          style={{...s.input, marginBottom: 12}}
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Type your password"
          required
        />

        <div style={s.forgotPasswordRow}>
          {!showReset ? (
            <a href="#" style={s.forgotPasswordLink} onClick={(e) => { e.preventDefault(); setShowReset(true); }}>
              Forgot your password?
            </a>
          ) : resetSent ? (
            <span style={{ color: "#e2e8f0", fontSize: 14 }}>
              If that address has an account, a reset link has been sent.
            </span>
          ) : (
            <div style={{ display: "flex", gap: 8, width: "100%" }}>
              <input
                style={{ ...s.input, marginBottom: 0, flex: 1, fontSize: 13, padding: "10px 12px" }}
                type="email"
                value={resetEmail}
                onChange={e => setResetEmail(e.target.value)}
                placeholder="Your email address"
              />
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={resetLoading}
                style={{ ...s.btn, width: "auto", padding: "10px 14px", fontSize: 13, whiteSpace: "nowrap", ...(resetLoading ? s.btnDisabled : {}) }}
              >
                {resetLoading ? "Sending…" : "Send link"}
              </button>
            </div>
          )}
        </div>

        <button
          type="submit"
          style={{ ...s.btn, ...((loading || !sessionReady) ? s.btnDisabled : {}) }}
          disabled={loading || !sessionReady}
          onMouseEnter={e => { if (!loading) e.target.style.background = "#e6ad03"; }}
          onMouseLeave={e => { e.target.style.background = "#fabd05"; }}
        >
          {!sessionReady ? "Ending Previous Session…" : loading ? "Logging In..." : "Log In"}
        </button>
      </form>
    </div>
  );
}
